<template>
  <v-container>
    <!-- Show question name -->
    <h3 class="mb-2">
      {{ questionData.name }}
    </h3>

    <v-row
      v-if="questionData.pictureUrl !== '' || questionData.videoUrl !== ''"
      justify="center"
      class="ma-0 mt-3 mb-4"
    >
      <!-- Show picture or video if not empty -->
      <v-img
        v-if="questionData.pictureUrl !== ''"
        class="image"
        :src="questionData.pictureUrl"
      />

      <video v-if="questionData.videoUrl !== ''" controls class="video">
        <source :src="questionData.videoUrl" />
      </video>
    </v-row>

    <div class="pa-0" v-html="questionData.description" />

    <!-- Text is the answerType of this question -->
    <v-textarea
      v-if="questionData.answerType === 'Text'"
      v-model="questionData.givenAnswer"
      :rules="textFieldRules"
      ref="textField"
      :label="$translate('QUIZ_TEXT_LABEL')"
      :maxlength="2000"
      :counter="2000"
      outlined
      clearable
      rows="3"
      @change="textFieldChange"
    />

    <!-- Number is the answerType of this question -->
    <v-text-field
      v-if="questionData.answerType === 'Number'"
      v-model="questionData.givenAnswer"
      :rules="textFieldRules"
      ref="numberField"
      :label="$translate('QUIZ_NUMBER_LABEL')"
      outlined
      clearable
      type="number"
      v-on:keyup.enter="onEnterPressed($event)"
      @change="numberFieldChange"
    />

    <!-- Picture is the answerType of this question -->
    <ImageLoader
      v-if="questionData.answerType === 'Picture'"
      :imageProps="imageProps"
      v-on:uploadImage="onImageChange"
      class="form-image-container"
    />
    <v-row
      justify="center"
      class="ma-4"
      no-gutters
      v-if="
        questionData.answerType === 'Picture' && questionData.base64image === ''
      "
    >
      <p style="font-size: 12px; color: red">
        {{ $translate('QUIZ_MUST_CHOOSE_PHOTO') }}
      </p>
    </v-row>

    <VideoLoader
      v-if="questionData.answerType === 'Video'"
      :config="videoProps"
      @change="onVideoChange"
    />
    <v-row
      justify="center"
      class="ma-4"
      no-gutters
      v-if="questionData.answerType === 'Video' && questionData.videoId === ''"
    >
      <p style="font-size: 12px; color: red">
        {{ $translate('QUIZ_MUST_CHOOSE_VIDEO') }}
      </p>
    </v-row>

    <!-- MultiChoice is the answerType of this question -->
    <v-radio-group
      v-if="questionData.answerType === 'MultiChoice'"
      v-model="questionData.givenAnswer"
      :rules="radioGroupRules"
      ref="radioGroup"
      hide-details="auto"
      column
      class="ma-0 pa-0"
      @change="onMultipleChoiceSelected"
    >
      <v-radio
        v-for="(item, index) in questionData.answers"
        :key="index"
        :value="index"
      >
        <template v-slot:label>
          <div class="text-subtitle-1 text--primary font-weight-medium">
            {{ item }}
          </div>
        </template>
      </v-radio>
    </v-radio-group>
  </v-container>
</template>

<script>
import { QuestionData } from '@/objects/QuizQuestionDataModel';
import ImageLoader from '@/components/generalComponents/media/ImageLoader';
import VideoLoader from '@/components/generalComponents/media/VideoLoader';

export default {
  name: 'Question',
  props: {
    // Use v-model on this component to bind the data bi-directional between parent and child.
    value: QuestionData,
  },
  components: {
    ImageLoader,
    VideoLoader,
  },
  // Watch the changes of the parent on the data passed as props.
  watch: {
    value() {
      this.init();
    },
    quizQuestionDataModel() {
      this.init();
    },
  },
  data() {
    return {
      questionData: QuestionData,
      imageProps: {},
      videoProps: {},
      radioGroupRules: [
        (v) => (!!v && v !== '') || this.$translate('QUIZ_MUST_CHOOSE_MULTI'),
      ],
      textFieldRules: [
        (v) => (!!v && v !== '') || this.$translate('QUIZ_MUST_FILL_IN'),
      ],
    };
  },
  beforeMount() {
    this.init();
  },
  methods: {
    init() {
      this.questionData = this.value;
      this.imageProps = {
        id: 'quizQuestionImageLoader',
        src: '',
      };
      this.videoProps = {
        src: '',
      };
    },
    onMultipleChoiceSelected() {
      console.log(`MultiChoice selected: ${this.questionData.givenAnswer}`);
      this.$refs.radioGroup.resetValidation();
    },
    onEnterPressed(event) {
      console.log(`Text entered: ${this.questionData.givenAnswer}`);
      event.target.blur(); // Lose focus on text field after enter is pressed.
    },
    textFieldChange() {
      this.$refs.textField.resetValidation();
    },
    numberFieldChange() {
      this.$refs.numberField.resetValidation();
    },
    onImageChange(image) {
      this.questionData.base64image = image;
      this.imageProps.src = image;
    },
    onVideoChange(videoUpdate) {
      this.questionData.videoId = videoUpdate.FileId;
    },
  },
};
</script>

<style lang="scss" scoped>
.video {
  width: 100%;
  height: auto;
  max-height: 80vh;
}
.image {
  width: 100%;
  height: auto;
  max-height: 80vh;
}
</style>
